import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useGoHighLevelCRUDActions } from '@mm/state/actions/GoHighLevelCRUDActions';
import { selectGoHighLevelConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useGuardedConnectOAuthToken } from '@mm/stateful-components/OAuth/use-guarded-connect-o-auth-token';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export const useConnectGoHighLevel = () => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const goHighLevelConnections = useSelector(selectGoHighLevelConnections);

  const { getGoHighLevelOauthUrl, getAccounts, getLocations, revokeAccess } =
    useGoHighLevelCRUDActions();
  const { retrieveAll, update, create } = useConnectionCRUDActions();

  const fetchOAuthUrl = useCallback(
    async (tokenId?: number) => {
      const link = await getGoHighLevelOauthUrl(tokenId);
      return link;
    },
    [getGoHighLevelOauthUrl],
  );

  const { connect: connectGoHighLevelToken } = useGuardedConnectOAuthToken(
    'nativeIntegrations',
    landmineNames.GOHIGHLEVEL_UPGRADE,
    'gohighlevelConnect',
    'gohighlevelTokenReceived',
    fetchOAuthUrl,
    async () => {
      const accountsResponse = await getGoHighLevelAccounts();
      const { id } = accountsResponse[0];
      getLocations(facebookPageId, id, true);
      if (!goHighLevelConnections.length && hasNativeIntegrationsFeature) {
        create(facebookPageId, {
          name: 'GoHighLevel Connection',
          connectionKind: 'gohighlevel',
          gohighlevelParams: { oauthTokenId: id },
        });
      } else if (
        (goHighLevelConnections || []).find(
          conn => conn.gohighlevelOauthToken?.id !== id,
        )
      ) {
        goHighLevelConnections.forEach(connection => {
          update(facebookPageId, {
            id: connection.id,
            gohighlevelParams: { oauthTokenId: id },
          });
        });
      }
    },
    { width: 600, height: 600 },
  );

  const getGoHighLevelAccounts = async () => getAccounts(facebookPageId);

  const getGoHighLevelConnections = async () => {
    retrieveAll(facebookPageId, {
      type: 'gohighlevel',
      pageSize: 200,
    });
  };

  const revoke = async (id: number) => {
    const result = await revokeAccess(id);
    if (result) {
      getGoHighLevelConnections();
    }
  };

  const refreshConnections = async () => {
    retrieveAll(facebookPageId, {
      type: 'gohighlevel',
      pageSize: 200,
    });
  };

  return {
    getGoHighLevelLocations: getLocations,
    getGoHighLevelAccounts,
    refreshConnections,
    goHighLevelConnections,
    getGoHighLevelConnections,
    connectGoHighLevelToken,
    revoke,
  };
};
